import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Truck, Plus, X, Package, AlertTriangle, Search,
  Building2, Tag, Calendar, AlertCircle, Link2
} from "lucide-react";

export default function Traceability() {
  const { currentVenue } = useVenue();
  const [suppliers, setSuppliers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [recalls, setRecalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSupForm, setShowSupForm] = useState(false);
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [showRecallForm, setShowRecallForm] = useState(false);
  const [supForm, setSupForm] = useState({ name: "", contact: "", phone: "", products: "", certifications: "" });
  const [batchForm, setBatchForm] = useState({ supplier_name: "", product_name: "", lot_number: "", received_date: "", expiry_date: "", quantity: "" });
  const [recallForm, setRecallForm] = useState({ product_name: "", lot_number: "", reason: "", supplier_name: "" });

  useEffect(() => { if (currentVenue) loadAll(); }, [currentVenue]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, b, r] = await Promise.all([
        hosys.entities.getAll("suppliers", currentVenue.id, "-created_date"),
        hosys.entities.getAll("product_batches", currentVenue.id, "-received_date"),
        hosys.entities.getAll("recall_alerts", currentVenue.id, "-created_date"),
      ]);
      setSuppliers(s);
      setBatches(b);
      setRecalls(r);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAddSupplier = async () => {
    if (!supForm.name) return;
    await hosys.entities.create("suppliers", {
      ...supForm, venue_id: currentVenue.id, active: true,
      products: supForm.products.split(",").map(p => p.trim()).filter(Boolean),
      certifications: supForm.certifications.split(",").map(c => c.trim()).filter(Boolean),
    });
    setShowSupForm(false);
    setSupForm({ name: "", contact: "", phone: "", products: "", certifications: "" });
    loadAll();
  };

  const handleAddBatch = async () => {
    if (!batchForm.product_name || !batchForm.lot_number) return;
    await hosys.entities.create("product_batches", {
      ...batchForm, venue_id: currentVenue.id, status: "active",
    });
    setShowBatchForm(false);
    setBatchForm({ supplier_name: "", product_name: "", lot_number: "", received_date: "", expiry_date: "", quantity: "" });
    loadAll();
  };

  const handleCreateRecall = async () => {
    if (!recallForm.product_name || !recallForm.reason) return;
    await hosys.entities.create("recall_alerts", {
      ...recallForm, venue_id: currentVenue.id, status: "active",
      date: new Date().toISOString().slice(0, 10),
    });
    setShowRecallForm(false);
    setRecallForm({ product_name: "", lot_number: "", reason: "", supplier_name: "" });
    loadAll();
  };

  const handleResolveRecall = async (recall) => {
    await hosys.entities.update("recall_alerts", recall.id || recall.recall_alert_id, { status: "resolved" });
    loadAll();
  };

  if (loading) return <QuickLoader />;

  const today = new Date().toISOString().slice(0, 10);
  const expiredBatches = batches.filter(b => b.expiry_date && b.expiry_date < today && b.status !== "consumed");
  const activeRecalls = recalls.filter(r => r.status === "active");

  return (
    <div className="space-y-5" data-testid="traceability-page">
      <PageHeader title="Traçabilitat" subtitle="Control de proveïdors, lots i sistema de retirada" />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 border-0 shadow-sm bg-blue-50 text-center">
          <p className="text-2xl font-bold text-blue-700">{suppliers.length}</p>
          <p className="text-xs text-gray-500">Proveïdors</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm bg-purple-50 text-center">
          <p className="text-2xl font-bold text-purple-700">{batches.length}</p>
          <p className="text-xs text-gray-500">Lots</p>
        </Card>
        <Card className={`p-4 border-0 shadow-sm text-center ${expiredBatches.length > 0 ? "bg-amber-50" : "bg-emerald-50"}`}>
          <p className={`text-2xl font-bold ${expiredBatches.length > 0 ? "text-amber-700" : "text-emerald-700"}`}>{expiredBatches.length}</p>
          <p className="text-xs text-gray-500">Caducats</p>
        </Card>
        <Card className={`p-4 border-0 shadow-sm text-center ${activeRecalls.length > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
          <p className={`text-2xl font-bold ${activeRecalls.length > 0 ? "text-red-700" : "text-emerald-700"}`}>{activeRecalls.length}</p>
          <p className="text-xs text-gray-500">Alertes retirada</p>
        </Card>
      </div>

      {/* Active Recalls Alert */}
      {activeRecalls.length > 0 && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="recall-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800">{activeRecalls.length} alerta(es) de retirada activa(es)</p>
            {activeRecalls.map((r, i) => (
              <div key={`trace-${i}`} className="flex items-center justify-between mt-1">
                <p className="text-xs text-red-600">{r.product_name} (Lot: {r.lot_number || "N/A"}) — {r.reason}</p>
                <button onClick={() => handleResolveRecall(r)} className="text-xs text-emerald-600 hover:bg-emerald-50 px-2 py-0.5 rounded" data-testid={`resolve-recall-${i}`}>Resolta</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="suppliers">
        <TabsList className="grid grid-cols-3 h-auto gap-1 p-1 bg-gray-100 rounded-xl">
          <TabsTrigger value="suppliers" className="rounded-lg text-xs py-2"><Building2 className="w-3.5 h-3.5 mr-1 inline" />Proveïdors</TabsTrigger>
          <TabsTrigger value="batches" className="rounded-lg text-xs py-2"><Package className="w-3.5 h-3.5 mr-1 inline" />Lots</TabsTrigger>
          <TabsTrigger value="recalls" className="rounded-lg text-xs py-2"><AlertCircle className="w-3.5 h-3.5 mr-1 inline" />Retirades</TabsTrigger>
        </TabsList>

        {/* Suppliers */}
        <TabsContent value="suppliers" className="mt-4 space-y-3">
          <Button onClick={() => setShowSupForm(!showSupForm)} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="new-supplier-btn">
            <Plus className="w-4 h-4 mr-1" /> Nou proveïdor
          </Button>
          {showSupForm && (
            <Card className="p-4 border-0 shadow-sm" data-testid="supplier-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <Input placeholder="Nom del proveïdor" value={supForm.name} onChange={e => setSupForm({...supForm, name: e.target.value})} data-testid="supplier-name" />
                <Input placeholder="Persona de contacte" value={supForm.contact} onChange={e => setSupForm({...supForm, contact: e.target.value})} />
                <Input placeholder="Telèfon" value={supForm.phone} onChange={e => setSupForm({...supForm, phone: e.target.value})} />
                <Input placeholder="Productes (separats per coma)" value={supForm.products} onChange={e => setSupForm({...supForm, products: e.target.value})} />
              </div>
              <Input placeholder="Certificacions (separades per coma)" className="mb-3" value={supForm.certifications} onChange={e => setSupForm({...supForm, certifications: e.target.value})} />
              <Button onClick={handleAddSupplier} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="supplier-submit">Guardar</Button>
            </Card>
          )}
          {suppliers.length === 0 ? (
            <Card className="p-8 border-0 shadow-sm text-center"><Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">Cap proveïdor registrat.</p></Card>
          ) : suppliers.map((s, i) => (
            <Card key={s.id || s.supplier_id || i} className="p-4 border-0 shadow-sm" data-testid={`supplier-${i}`}>
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-blue-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{s.name}</p>
                  {s.contact && <p className="text-xs text-gray-500">{s.contact} {s.phone ? `· ${s.phone}` : ""}</p>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(s.products || []).map((p, j) => <Badge key={`el-${j}`} className="bg-blue-50 text-blue-700 border-0 text-[10px]">{p}</Badge>)}
                    {(s.certifications || []).map((c, j) => <Badge key={`el-${j}`} className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">{c}</Badge>)}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        {/* Batches */}
        <TabsContent value="batches" className="mt-4 space-y-3">
          <Button onClick={() => setShowBatchForm(!showBatchForm)} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="new-batch-btn">
            <Plus className="w-4 h-4 mr-1" /> Nou lot
          </Button>
          {showBatchForm && (
            <Card className="p-4 border-0 shadow-sm" data-testid="batch-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <Input placeholder="Producte" value={batchForm.product_name} onChange={e => setBatchForm({...batchForm, product_name: e.target.value})} data-testid="batch-product" />
                <Input placeholder="Núm. lot" value={batchForm.lot_number} onChange={e => setBatchForm({...batchForm, lot_number: e.target.value})} data-testid="batch-lot" />
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={batchForm.supplier_name} onChange={e => setBatchForm({...batchForm, supplier_name: e.target.value})}>
                  <option value="">Proveïdor...</option>
                  {suppliers.map((s, i) => <option key={s.name || i} value={s.name}>{s.name}</option>)}
                </select>
                <Input type="date" value={batchForm.received_date} onChange={e => setBatchForm({...batchForm, received_date: e.target.value})} />
                <Input type="date" value={batchForm.expiry_date} onChange={e => setBatchForm({...batchForm, expiry_date: e.target.value})} />
                <Input placeholder="Quantitat" value={batchForm.quantity} onChange={e => setBatchForm({...batchForm, quantity: e.target.value})} />
              </div>
              <Button onClick={handleAddBatch} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="batch-submit">Registrar lot</Button>
            </Card>
          )}
          {batches.length === 0 ? (
            <Card className="p-8 border-0 shadow-sm text-center"><Package className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">Cap lot registrat.</p></Card>
          ) : batches.map((b, i) => {
            const isExpired = b.expiry_date && b.expiry_date < today && b.status !== "consumed";
            return (
              <Card key={b.id || b.product_batch_id || i} className={`p-4 border-0 shadow-sm ${isExpired ? "border-l-4 border-l-red-500" : ""}`} data-testid={`batch-${i}`}>
                <div className="flex items-center gap-3">
                  <Package className={`w-5 h-5 ${isExpired ? "text-red-500" : "text-purple-500"}`} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{b.product_name}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">Lot: {b.lot_number}</Badge>
                      {b.supplier_name && <Badge className="bg-blue-50 text-blue-700 border-0 text-[10px]">{b.supplier_name}</Badge>}
                      {isExpired && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">CADUCAT</Badge>}
                      {b.quantity && <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">Qty: {b.quantity}</Badge>}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-500 flex-shrink-0">
                    {b.received_date && <p>Rebut: {b.received_date}</p>}
                    {b.expiry_date && <p className={isExpired ? "text-red-600 font-semibold" : ""}>Caduca: {b.expiry_date}</p>}
                  </div>
                </div>
              </Card>
            );
          })}
        </TabsContent>

        {/* Recalls */}
        <TabsContent value="recalls" className="mt-4 space-y-3">
          <Button onClick={() => setShowRecallForm(!showRecallForm)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="new-recall-btn">
            <AlertTriangle className="w-4 h-4 mr-1" /> Nova alerta de retirada
          </Button>
          {showRecallForm && (
            <Card className="p-4 border-0 shadow-sm border-l-4 border-l-red-500" data-testid="recall-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <Input placeholder="Producte afectat" value={recallForm.product_name} onChange={e => setRecallForm({...recallForm, product_name: e.target.value})} data-testid="recall-product" />
                <Input placeholder="Núm. lot (opcional)" value={recallForm.lot_number} onChange={e => setRecallForm({...recallForm, lot_number: e.target.value})} />
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={recallForm.supplier_name} onChange={e => setRecallForm({...recallForm, supplier_name: e.target.value})}>
                  <option value="">Proveïdor...</option>
                  {suppliers.map((s, i) => <option key={s.name || i} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <Textarea placeholder="Motiu de la retirada..." className="mb-3" value={recallForm.reason} onChange={e => setRecallForm({...recallForm, reason: e.target.value})} data-testid="recall-reason" />
              <Button onClick={handleCreateRecall} className="bg-red-600 hover:bg-red-700 text-white" data-testid="recall-submit">Crear alerta</Button>
            </Card>
          )}
          {recalls.length === 0 ? (
            <Card className="p-8 border-0 shadow-sm text-center"><AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-sm text-gray-500">Cap alerta de retirada.</p></Card>
          ) : recalls.map((r, i) => {
            const isActive = r.status === "active";
            return (
              <Card key={r.id || r.recall_alert_id || i} className={`p-4 border-0 shadow-sm ${isActive ? "border-l-4 border-l-red-500" : "opacity-60"}`} data-testid={`recall-${i}`}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-5 h-5 ${isActive ? "text-red-600" : "text-gray-400"}`} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{r.product_name}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{r.reason}</p>
                    <div className="flex gap-1 mt-1">
                      {r.lot_number && <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">Lot: {r.lot_number}</Badge>}
                      {r.supplier_name && <Badge className="bg-blue-50 text-blue-700 border-0 text-[10px]">{r.supplier_name}</Badge>}
                      <Badge className={`border-0 text-[10px] ${isActive ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>{isActive ? "Activa" : "Resolta"}</Badge>
                    </div>
                  </div>
                  {isActive && <button onClick={() => handleResolveRecall(r)} className="text-xs text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded-lg" data-testid={`resolve-recall-${i}`}>Resolta</button>}
                </div>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>
    </div>
  );
}
